import { useState, useMemo, type ReactNode, type ComponentType } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTimePrecise } from '@shared-ui/components';
import { adminApi, userApi } from '../../api/adminApi';
import type { ModelInput, ModelOutput, Feedback, AttachedImage } from '../../types';
import type { PromptInfo, PromptType, PromptVersionInfo, PromptInputMeta } from '../../types/settings';
import { stepTypeFamily, getPromptInputMeta } from '../../types/settings';
import { buildQuestionDisplayText, buildQuestionCorrectAnswerText, correctnessFromScore, findSubstance } from '@padcanvas/shared';
import { SCORING_LABELS } from '@shared-ui/scoring-labels';
import { ImageViewer, JsonViewerButton, TextViewerButton, IdControl } from '@shared-ui/components';
import { ModelOutputView, RunStatusCell, runLiveState } from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, resolveOutputSchemaType, getRawOutputText } from '@shared-ui/parsed-feedback';
import { QualityCheckBadges } from '../operations/parsedFeedback';
import { StudentViewSection } from './StudentViewSection';
import { ModelRunReplays, ReplayBanner, ReplayButton } from './ModelRunReplayPanel';
import { RelatedModelRunsDialog } from './RelatedModelRunsDialog';
import { triggerLabel } from '../../types';
import { sentStudentImage, sentQuestionImage, sentStudentImagesOf } from '../../utils/attachedImages';
import { BackButton } from '@shared-ui/components';
import './ModelRunDetail.css';
import '../operations/SolutionAnalysisDetail.css';

// ── Formatting ───────────────────────────────────────────────

const formatLatency = (ms: number) =>
  ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;

const formatDuration = (ms: number) => {
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return m === 0 ? `${s}s` : `${m}m ${s}s`;
};

const formatCost = (cost?: number) =>
  cost == null ? '-' : `$${cost.toFixed(4)}`;

const ASSESSMENT_CLASSES: Record<string, string> = {
  correct: 'correct', partially_correct: 'partial',
  incorrect: 'incorrect', correct_but_inefficient: 'partial',
};
const STATUS_CLASSES: Record<string, string> = {
  completed: 'correct', error: 'incorrect', pending: 'partial',
};
const badgeClass = (value: string | undefined, map: Record<string, string>) =>
  (value && map[value]) || '';

// ── Reusable UI Primitives ───────────────────────────────────

/** Sent/not-sent tag for a question or part diagram in the Input Details
 *  image blocks. A diagram was sent iff `attachedImages` carries an entry with
 *  the question role and this label. Absent on rows written before image
 *  recording — shown as "not sent". */
function QuestionImageSentTag({ attachedImages, label }: {
  attachedImages?: AttachedImage[]; label: string;
}) {
  const sent = sentQuestionImage(attachedImages, label);
  return (
    <span
      className="tag"
      style={sent
        ? { backgroundColor: 'var(--status-success-bg)', color: 'var(--status-success-text)' }
        : { backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}
      title={sent
        ? `This diagram was attached to the LLM call, labeled "${label}".`
        : 'This diagram was NOT attached to the LLM call — the prompt does not reference {{q.text}}, or this run predates question-image attachment. Shown for reference only.'}
    >
      {sent ? '✓ sent to model' : 'not sent to model'}
    </span>
  );
}

function CollapsibleSection({ id, title, collapsed, onToggle, style, titleAccessory, children }: {
  id: string; title: string; collapsed: boolean; onToggle: (id: string) => void;
  style?: React.CSSProperties; titleAccessory?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="op-collapsible-box" style={style}>
      <div className="section-toggle-row">
        <button className="section-toggle" onClick={() => onToggle(id)}>
          <span className={`toggle-icon ${collapsed ? 'collapsed' : ''}`}>&#9660;</span>
          <span>{title}</span>
        </button>
        {titleAccessory && <span className="section-toggle-accessory">{titleAccessory}</span>}
      </div>
      {!collapsed && children}
    </div>
  );
}

// ── Input Details (metadata-driven) ──────────────────────────
//
// The panel reflects the prompt-input registry (Backend/shared/src/placeholders.ts):
// only inputs allowed for this run's prompt type are shown, and only those whose
// values are actually snapshotted on the ModelInput record. Inputs computed at
// template-render time from pipeline context (sol.*, hint.*, analysis.*,
// stdsol.solution / stdsol.extractedSteps / stdsol.extractedVisuals*,
// module.instructions, conv.*, lm.candidates) are intentionally skipped — their
// values aren't on the ModelInput doc, so we have nothing to show.

type PromptInputName = PromptInputMeta['name'];

const INPUT_LABELS: Partial<Record<PromptInputName, string>> = {
  'q.subject': 'Subject',
  'q.topic': 'Topic',
  'q.difficulty': 'Difficulty',
  'q.text': 'Question Text',
  'q.correctAnswer': 'Correct Answer',
  'q.type': 'Type',
  'std.class': 'Class',
  'std.proficiency': 'Proficiency',
  'std.targetExamForSubmission': 'Target Exam',
  'stdsol.imageAnswer': 'Image Answer',
  'stdsol.textAnswer': 'Text Answer',
  'stdsol.selectedOption': 'Selected Option',
  'stdsol.actualTime': 'Time Taken',
  'stdsol.actualTimeSeconds': 'Time Taken (s)',
};

const GROUP_LABELS: Record<string, string> = {
  Question: 'Question',
  Solution: 'Solution',
  Student: 'Student',
  StudentSolution: 'Student Solution',
  Hint: 'Hint Context',
  Analysis: 'Analysis',
  Module: 'Module',
  TextChat: 'Conversation',
  LearningMoments: 'Learning Moments',
};

/** Resolution result for a single prompt input.
 *  - `value`   — snapshotted on ModelInput and present.
 *  - `empty`   — snapshotted but the field is missing/blank on this run.
 *  - `runtime` — not snapshotted on ModelInput; resolved at template-render
 *                time from pipeline context (sol.*, hint.*, analysis.*, etc.).
 */
type InputResolution =
  | { kind: 'value'; value: string | number }
  | { kind: 'empty' }
  | { kind: 'runtime' };

function resolveInputValue(name: PromptInputName, input: ModelInput): InputResolution {
  const wrap = (v: string | number | undefined | null): InputResolution =>
    v === undefined || v === null || v === '' ? { kind: 'empty' } : { kind: 'value', value: v };
  switch (name) {
    case 'q.subject':        return wrap(input.question?.subject);
    case 'q.topic':          return wrap(input.question?.topic);
    case 'q.difficulty':     return wrap(input.question?.difficulty);
    // q.text / q.correctAnswer mirror the prompt expansion for STRUCTURED
    // questions: part statements are appended to the stem, and per-part
    // answers compose into "(a) …" lines (shared helpers — same rendering
    // as question lists/previews).
    case 'q.text':
      return wrap(buildQuestionDisplayText({
        questionType: input.question?.questionType,
        questionText: input.question?.text,
        parts: input.question?.parts,
      }));
    case 'q.correctAnswer':  return wrap(buildQuestionCorrectAnswerText(input.question ?? {}));
    case 'q.type':           return wrap(input.question?.questionType);
    case 'std.class':        return wrap(input.studentProfile?.class);
    case 'std.proficiency':  return wrap(input.studentProfile?.proficiency);
    case 'std.targetExamForSubmission': return wrap(input.studentProfile?.targetExam);
    case 'stdsol.imageAnswer':
      return input.studentSolution?.imageUrl
        ? { kind: 'value', value: '(image attached — see thumbnail above)' }
        : { kind: 'empty' };
    case 'stdsol.textAnswer':     return wrap(input.studentSolution?.textAnswer);
    case 'stdsol.selectedOption': return wrap(input.studentSolution?.selectedOption);
    case 'stdsol.actualTime':
    case 'stdsol.actualTimeSeconds':
      return typeof input.studentSolution?.timeTakenSeconds === 'number'
        ? { kind: 'value', value: `${input.studentSolution?.timeTakenSeconds}s` }
        : { kind: 'empty' };
    default:
      return { kind: 'runtime' };
  }
}

/** Snapshotted-only names — used as the fallback name list when promptType is
 *  unknown (legacy runs). */
const SNAPSHOTTED_INPUT_NAMES: ReadonlyArray<PromptInputName> = [
  'q.subject', 'q.topic', 'q.difficulty', 'q.text', 'q.correctAnswer', 'q.type',
  'std.class', 'std.proficiency', 'std.targetExamForSubmission',
  'stdsol.imageAnswer', 'stdsol.textAnswer', 'stdsol.selectedOption',
  'stdsol.actualTimeSeconds',
];

function PromptInputView({ input, promptType, hideGroups }: {
  input: ModelInput; promptType?: PromptType;
  /** Groups to omit even when the type allows them — e.g. the empty Question
   *  group that the global q.* wildcard pulls into a session-report run. */
  hideGroups?: ReadonlySet<string>;
}) {
  // Behaviour:
  //   • promptType present → list every input the type allows, even when empty
  //     or runtime-resolved. Admin can see exactly what the template can pull
  //     and what's actually populated on this run.
  //   • promptType missing (legacy) → show only snapshotted inputs that have a
  //     value. We can't trust the prompt-input registry to be exhaustive for
  //     unknown types, so collapse to the safe set.
  const orderedMetas: PromptInputMeta[] = promptType
    ? getPromptInputMeta(promptType)
    : SNAPSHOTTED_INPUT_NAMES.map(name => ({
        name,
        tag: `{{${name}}}`,
        group: name.startsWith('q.') ? 'Question' : name.startsWith('std.') ? 'Student' : 'StudentSolution',
        description: '',
        cardinality: 'low' as const,
        required: false,
      }));

  type Resolved = { meta: PromptInputMeta; resolution: InputResolution };
  const groups = new Map<string, Resolved[]>();

  for (const meta of orderedMetas) {
    if (hideGroups?.has(meta.group)) continue;
    const resolution = resolveInputValue(meta.name, input);
    if (!promptType && resolution.kind !== 'value') continue;  // legacy: only show populated
    if (!groups.has(meta.group)) groups.set(meta.group, []);
    groups.get(meta.group)!.push({ meta, resolution });
  }

  if (groups.size === 0) {
    return <p className="empty">No prompt inputs to display for this run.</p>;
  }

  // Returns a fragment of fieldsets — caller wraps with the .input-context
  // flex container so other subsections (e.g. Input Image) can sit alongside.
  return (
    <>
      {Array.from(groups.entries()).map(([group, fields]) => {
        const inline = fields.filter(f => f.meta.name !== 'q.text');
        const questionText = fields.find(f => f.meta.name === 'q.text');
        return (
          <fieldset key={group} className="prompt-input-group">
            <legend>
              {GROUP_LABELS[group] || group}
              {group === 'Question' && input.question?.id && (
                <span style={{ marginLeft: 8, fontWeight: 400 }}>
                  <IdControl value={input.question?.id ?? ''} container="questions" />
                </span>
              )}
            </legend>
            {inline.length > 0 && (
              <div className="detail-grid">
                {inline.map(f => (
                  <div key={f.meta.name} title={f.meta.description || undefined}>
                    <strong>{INPUT_LABELS[f.meta.name] || f.meta.name}:</strong>{' '}
                    <PromptInputValue resolution={f.resolution} required={f.meta.required} />
                  </div>
                ))}
              </div>
            )}
            {questionText && (
              <div title={questionText.meta.description || undefined}>
                {questionText.resolution.kind === 'value'
                  ? <div className="question-text-full">{String(questionText.resolution.value)}</div>
                  : <div><strong>{INPUT_LABELS['q.text']}:</strong>{' '}
                      <PromptInputValue resolution={questionText.resolution} required={questionText.meta.required} />
                    </div>}
              </div>
            )}
          </fieldset>
        );
      })}
    </>
  );
}

// ── Chemistry lab inputs ─────────────────────────────────────
//
// A lab mix has no question, student or image: the model is told which
// substances meet, how hot, and in which phase. The ModelInput keeps that as
// ids on `operationContext`, so names and formulas come from the same catalog
// the bench uses.

const CHEMISTRY_LAB_PROMPT_TYPES: ReadonlySet<string> = new Set(['chemistry_lab_mix', 'chemistry_lab_excess']);
const CHEMISTRY_LAB_OPERATIONS: ReadonlySet<string> = new Set(['chemistryLabMix', 'chemistryLabExcess']);

interface ChemistryLabContext {
  mixKey?: string;
  excessKey?: string;
  substanceIds?: string[];
  temperatureBand?: string;
  phase?: string;
  /** Excess runs: the reagent in excess, and by how much. */
  formula?: string;
  band?: string;
}

function ChemistryLabInputView({ input }: { input: ModelInput }) {
  const ctx = (input.operationContext ?? {}) as ChemistryLabContext;
  const ids = ctx.substanceIds ?? [];
  const isExcess = input.operation === 'chemistryLabExcess' || ctx.excessKey !== undefined;
  const missing = <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>(not recorded)</span>;
  return (
    <>
      <fieldset className="prompt-input-group">
        <legend>Substances ({ids.length})</legend>
        {ids.length === 0
          ? <p className="empty">No substances recorded on this run.</p>
          : (
            <table className="chem-input-table">
              <thead>
                <tr><th>Substance</th><th>Formula</th><th>Category</th><th>Id</th></tr>
              </thead>
              <tbody>
                {ids.map((id, i) => {
                  const sub = findSubstance(id);
                  return (
                    <tr key={`${id}-${i}`}>
                      <td>{sub?.displayName ?? <span style={{ color: 'var(--color-warning-text)' }}>not in the catalog</span>}</td>
                      <td>{sub?.formula ?? '—'}</td>
                      <td>{sub?.category ?? '—'}</td>
                      <td><code>{id}</code></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </fieldset>
      <fieldset className="prompt-input-group">
        <legend>Conditions</legend>
        <div className="detail-grid">
          <div><strong>Temperature:</strong> {ctx.temperatureBand ?? missing}</div>
          {/* Phase is recorded only when it is vapour; absent means the ordinary liquid mix. */}
          {!isExcess && <div><strong>Phase:</strong> {ctx.phase ?? 'liquid'}</div>}
          {isExcess && <div><strong>Excess reagent:</strong> {ctx.formula ?? missing}</div>}
          {isExcess && <div><strong>Excess band:</strong> {ctx.band ?? missing}</div>}
          <div>
            <strong>{isExcess ? 'Excess key' : 'Mix key'}:</strong>{' '}
            {(isExcess ? ctx.excessKey : ctx.mixKey) ? <code>{isExcess ? ctx.excessKey : ctx.mixKey}</code> : missing}
          </div>
        </div>
      </fieldset>
    </>
  );
}

/** Runs that read a teacher's document — the paper, or its answer key. They
 *  have no student, no question and no assignment: the generic Input Details
 *  rendered a page of "empty — required" placeholders around the one input
 *  that matters, the pages the model was shown. */
const EXTRACTION_PROMPT_TYPES: ReadonlySet<string> = new Set([
  'extract_verbatim', 'extract_verbatim_structured',
  'extract_verbatim_text', 'extract_verbatim_text_structured',
  'validate_extraction', 'locate_diagrams', 'extract_answer_key',
]);

/** What extraction records on `operationContext` (extractQuestionsFromPdfJob's
 *  recordModelRun). */
interface ExtractionContext {
  sourceKind?: string;
  sourceFileName?: string;
  runInfo?: string;
  crossCheckProvider?: string;
  noMarks?: boolean;
  /** `context`: the next page, sent read-only after the target pages. */
  attachedImages?: { url: string; label: string; context?: boolean }[];
}

const SOURCE_KIND_LABELS: Record<string, string> = {
  pdf: 'PDF', images: 'Page images', docx: 'Word document',
};

function ExtractionInputView({ input, promptType, sentCount }: {
  input: ModelInput;
  promptType?: PromptType;
  /** Images the call carried, per the run's own record; undefined on old rows. */
  sentCount?: number;
}) {
  const ctx = (input.operationContext ?? {}) as ExtractionContext;
  const images = ctx.attachedImages ?? [];
  const isKey = promptType === 'extract_answer_key' || input.operation === 'extract_answer_key';
  const isJudge = promptType === 'validate_extraction';
  const isText = ctx.sourceKind === 'docx'
    || promptType === 'extract_verbatim_text' || promptType === 'extract_verbatim_text_structured';
  const secondRead = !!ctx.runInfo?.endsWith('(second read)');
  const targets = images.filter(img => !img.context);
  const context = images.filter(img => img.context);
  const missing = <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>(not recorded)</span>;
  return (
    <>
      <fieldset className="prompt-input-group">
        <legend>{isKey ? 'Answer Key' : 'Paper'}</legend>
        <div className="detail-grid">
          <div><strong>Document:</strong> {ctx.sourceFileName ?? missing}</div>
          <div><strong>Kind:</strong> {ctx.sourceKind ? (SOURCE_KIND_LABELS[ctx.sourceKind] ?? ctx.sourceKind) : missing}</div>
          <div><strong>This call:</strong> {ctx.runInfo ?? missing}</div>
          {isKey && <div><strong>Read:</strong> {secondRead ? 'second (the check)' : 'first'}</div>}
          {ctx.crossCheckProvider && <div><strong>Cross-check by:</strong> <code>{ctx.crossCheckProvider}</code></div>}
          {!isKey && <div><strong>Marking rules:</strong> {ctx.noMarks ? 'not read — an assignment has no marks' : 'read'}</div>}
          {isJudge && <div><strong>Also given:</strong> the extraction step’s transcription of these pages</div>}
        </div>
      </fieldset>
      <fieldset className="image-block">
        <legend>
          <span>Pages Sent To Model</span>
          {sentCount !== undefined && !isText && (
            <span
              className="tag"
              style={sentCount > 0
                ? { backgroundColor: 'var(--status-success-bg)', color: 'var(--status-success-text)' }
                : { backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}
              title="How many images this LLM call carried, as recorded on the run."
            >
              {sentCount > 0 ? `✓ ${sentCount} sent to model` : 'none sent'}
            </span>
          )}
        </legend>
        {isText ? (
          <div className="no-image-compact">
            A Word document is sent as text, not images — the text is in the prompt below.
          </div>
        ) : images.length === 0 ? (
          <div className="no-image-compact">
            {sentCount
              ? `${sentCount} image${sentCount === 1 ? ' was' : 's were'} sent, but not kept — ${isKey
                ? 'older answer-key reads did not store their pages'
                : 'the page upload failed or the run predates page recording'}.`
              : 'No images recorded for this call.'}
          </div>
        ) : (
          <div className="operation-images">
            {targets.map(img => (
              <figure key={img.label} className="operation-image">
                <ImageViewer src={img.url} alt={img.label} className="model-run-detail-thumb" />
                <figcaption>{img.label}</figcaption>
              </figure>
            ))}
            {context.map(img => (
              <figure key={img.label} className="operation-image" style={{ opacity: 0.7 }}>
                <ImageViewer src={img.url} alt={img.label} className="model-run-detail-thumb" />
                <figcaption>{img.label} — next page, read only</figcaption>
              </figure>
            ))}
          </div>
        )}
      </fieldset>
    </>
  );
}

function PromptInputValue({ resolution, required }: { resolution: InputResolution; required: boolean }) {
  if (resolution.kind === 'value') return <>{resolution.value}</>;
  const placeholder = resolution.kind === 'runtime'
    ? '(resolved at template render time)'
    : required
      ? '(empty — required by this prompt)'
      : '(empty)';
  const color = resolution.kind === 'empty' && required ? '#dc2626' : 'var(--text-tertiary)';
  return <span style={{ color, fontStyle: 'italic' }}>{placeholder}</span>;
}

// ── Parameters card helpers ──────────────────────────────────

const ADAPTER_LABELS: Record<string, string> = {
  'openai-responses': 'OpenAI Responses',
  'openai-chat-completions': 'OpenAI chat.completions',
  'anthropic-messages': 'Anthropic Messages',
  'gemini-generate-content': 'Gemini',
  'mock': 'Mock',
};

const ADAPTER_BADGE_COLOR: Record<string, string> = {
  'openai-responses': '#10b981',          // green — new path
  'openai-chat-completions': '#6b7280',   // grey — legacy
  'anthropic-messages': '#a78bfa',        // purple
  'gemini-generate-content': '#3b82f6',   // blue
  'mock': '#9ca3af',
};

function ActualParametersFields({ parameters }: { parameters: import('../../types').ModelOutputParameters }) {
  const actual = parameters.actual;

  // Legacy row (no `actual` field) — fall back to the bare temperature.
  if (!actual) {
    return (
      <>
        <div className="meta-item"><label>Temperature</label><span>{parameters.temperature ?? '-'}</span></div>
        <div className="meta-item"><label>Adapter</label><span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>(not recorded)</span></div>
      </>
    );
  }

  const family = actual.family;
  const adapterKind = family.kind;
  const adapterLabel = ADAPTER_LABELS[adapterKind] || adapterKind;
  const adapterColor = ADAPTER_BADGE_COLOR[adapterKind] || '#6b7280';

  return (
    <>
      <div className="meta-item">
        <label>Adapter</label>
        <span
          className="tag"
          style={{ backgroundColor: adapterColor, color: 'white', fontWeight: 600 }}
        >
          {adapterLabel}
        </span>
      </div>
      <div className="meta-item">
        <label>Temperature</label>
        <span>
          {actual.temperature === null
            ? <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>n/a (reasoning model)</span>
            : actual.temperature}
        </span>
      </div>
      {typeof actual.maxTokens === 'number' && (
        <div className="meta-item">
          <label>Max Tokens</label>
          <span>{actual.maxTokens.toLocaleString()}</span>
        </div>
      )}
      {actual.outputFormat && (
        <div className="meta-item">
          <label>Output Format</label>
          <span className="tag">{actual.outputFormat}</span>
        </div>
      )}
      {typeof actual.hasSystemPrompt === 'boolean' && (
        <div className="meta-item">
          <label>System Prompt</label>
          <span>{actual.hasSystemPrompt ? 'Yes' : 'No'}</span>
        </div>
      )}

      {/* Family-specific fields */}
      {family.kind === 'openai-responses' && (
        <>
          <div className="meta-item">
            <label>Reasoning Model</label>
            <span>{family.isReasoningModel ? 'Yes' : 'No'}</span>
          </div>
          {family.reasoningEffort && (
            <div className="meta-item">
              <label>Reasoning Effort</label>
              <span className="tag" style={{ backgroundColor: '#7c3aed', color: 'white', fontWeight: 600 }}>{family.reasoningEffort}</span>
            </div>
          )}
          {typeof family.reasoningSummary === 'boolean' && (
            <div className="meta-item">
              <label>Reasoning Summary</label>
              <span>{family.reasoningSummary ? 'On' : 'Off'}</span>
            </div>
          )}
          {family.jsonSchemaName && (
            <div className="meta-item">
              <label>JSON Schema</label>
              <span><code>{family.jsonSchemaName}</code></span>
            </div>
          )}
        </>
      )}
      {family.kind === 'openai-chat-completions' && (
        <div className="meta-item">
          <label>Token Param</label>
          <span><code>{family.tokenParamName}</code></span>
        </div>
      )}
      {family.kind === 'anthropic-messages' && family.prefillJson && (
        <div className="meta-item">
          <label>JSON Coercion</label>
          <span style={{ color: '#f59e0b' }}>prefill-{'{'} (legacy hack)</span>
        </div>
      )}
      {family.kind === 'anthropic-messages' && family.thinkingBudgetTokens !== undefined && (
        <div className="meta-item">
          <label>Thinking Budget</label>
          <span>{family.thinkingBudgetTokens.toLocaleString()} tokens</span>
        </div>
      )}
      {family.kind === 'gemini-generate-content' && family.responseMimeType && (
        <div className="meta-item">
          <label>Response MIME</label>
          <span><code>{family.responseMimeType}</code></span>
        </div>
      )}
      {family.kind === 'gemini-generate-content' && (
        <div className="meta-item">
          <label>Media Resolution</label>
          <span>
            {family.mediaResolution
              ? <span className="tag">{family.mediaResolution.replace('MEDIA_RESOLUTION_', '')}</span>
              : 'Default'}
            {typeof family.imageCount === 'number' && ` · ${family.imageCount} image${family.imageCount === 1 ? '' : 's'}`}
            {/* gemini-2.5 honours it only on a call with ONE image; the image
                tokens under Performance show what was actually charged. */}
            {family.mediaResolution && (family.imageCount ?? 0) > 1 && (
              <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}> — not applied by gemini-2.5 to a call with several images</span>
            )}
          </span>
        </div>
      )}
      {family.kind === 'gemini-generate-content' && family.thinkingBudgetTokens !== undefined && (
        <div className="meta-item">
          <label>Thinking Budget</label>
          <span>{family.thinkingBudgetTokens === 0
            ? 'Disabled (0)'
            : `${family.thinkingBudgetTokens.toLocaleString()} tokens`}
          </span>
        </div>
      )}
      {actual.tierRouting && <TierRoutingChain trace={actual.tierRouting} />}
    </>
  );
}

function TierRoutingChain({ trace }: { trace: import('../../types').TierRoutingTrace }) {
  const escalations = trace.escalations || [];
  return (
    <div className="meta-item" style={{ gridColumn: '1 / -1' }}>
      <label>Tier Routing</label>
      <div className="tier-routing-chain">
        <span className="tier-routing-set">{trace.providerSet}</span>
        <span className="tier-chip tier-chip--initial">{trace.initialTier}</span>
        {escalations.map((esc, i) => (
          <span key={i} className="tier-routing-step">
            <span className="tier-routing-arrow">→</span>
            <span className={`tier-chip tier-chip--${i === escalations.length - 1 && !trace.rejectedByEscalation ? 'final' : 'intermediate'}`}>
              {esc.toTier}
            </span>
            <span className="tier-routing-trigger" title={esc.previousResultExcerpt || ''}>({triggerLabel(esc.triggeredBy)})</span>
          </span>
        ))}
        {trace.rejectedByEscalation && <span className="tier-routing-rejected">rejected</span>}
      </div>
      {escalations.some(e => e.previousResultExcerpt) && (
        <details className="tier-routing-details">
          <summary>Rejected attempts</summary>
          {escalations.filter(e => e.previousResultExcerpt).map((e, i) => (
            <pre key={i} className="tier-routing-excerpt"><strong>{e.fromTier} → {e.toTier} ({triggerLabel(e.triggeredBy)})</strong>{'\n'}{e.previousResultExcerpt}</pre>
          ))}
        </details>
      )}
    </div>
  );
}

// ── Performance (per-type) ───────────────────────────────────

function TokenPerformance({ output }: { output: ModelOutput }) {
  const u = output.tokenUsage;
  // Derived breakdowns. Reasoning is a SUBSET of output_tokens (not additional);
  // cached is a SUBSET of input_tokens. We compute the "fresh" / "visible answer"
  // remainders so the math is transparent.
  const hasCached = typeof u.cached === 'number' && u.cached > 0;
  const hasReasoning = typeof u.reasoning === 'number' && u.reasoning > 0;
  const freshInput = hasCached ? u.prompt - (u.cached || 0) : null;
  const visibleAnswer = hasReasoning ? u.completion - (u.reasoning || 0) : null;
  return (
    <>
      <div className="perf-item"><label>Latency</label><span className="perf-value">{formatLatency(output.latencyMs)}</span></div>

      {/* Input breakdown */}
      <div className="perf-item"><label>Input Tokens</label><span className="perf-value">{u.prompt.toLocaleString()}</span></div>
      {hasCached && (
        <>
          <div className="perf-item">
            <label>↳ Cached Input</label>
            <span className="perf-value" style={{ color: '#10b981' }}>{(u.cached || 0).toLocaleString()}</span>
          </div>
          <div className="perf-item">
            <label>↳ Fresh Input</label>
            <span className="perf-value">{(freshInput || 0).toLocaleString()}</span>
          </div>
        </>
      )}
      {typeof u.image === 'number' && u.image > 0 && (
        <div className="perf-item">
          <label>↳ Image</label>
          <span className="perf-value" title="What the images were charged. gemini-2.5-flash reads an image at 258 tokens by default; more means a higher media resolution took effect.">
            {u.image.toLocaleString()}
          </span>
        </div>
      )}
      {typeof u.cacheCreation === 'number' && u.cacheCreation > 0 && (
        <div className="perf-item">
          <label>↳ Cache Write</label>
          <span className="perf-value" style={{ color: '#f59e0b' }}>{u.cacheCreation.toLocaleString()}</span>
        </div>
      )}

      {/* Output breakdown */}
      <div className="perf-item"><label>Output Tokens</label><span className="perf-value">{u.completion.toLocaleString()}</span></div>
      {hasReasoning && (
        <>
          <div className="perf-item">
            <label>↳ Reasoning</label>
            <span className="perf-value" style={{ color: '#a78bfa' }}>{(u.reasoning || 0).toLocaleString()}</span>
          </div>
          <div className="perf-item">
            <label>↳ Visible Answer</label>
            <span className="perf-value">{(visibleAnswer || 0).toLocaleString()}</span>
          </div>
        </>
      )}

      <div className="perf-item"><label>Total Tokens</label><span className="perf-value">{u.total.toLocaleString()}</span></div>
      <div className="perf-item"><label>Estimated Cost</label><span className="perf-value cost">{formatCost(output.cost)}</span></div>
    </>
  );
}

function DurationPerformance({ output }: { output: ModelOutput }) {
  return (
    <>
      <div className="perf-item"><label>Duration</label><span className="perf-value">{formatDuration(output.latencyMs)}</span></div>
      <div className="perf-item"><label>Estimated Cost</label><span className="perf-value cost">{formatCost(output.cost)}</span></div>
    </>
  );
}

// ── Parsed Output (per-OutputSchemaType, shared with Submission Detail) ──

/** Header chips for analyze runs — score + sub-scores.
 *  Rendered above the schema-specific view body so the run-level summary
 *  is visible at a glance before drilling into steps/checks.
 *  Color class is now score-derived (>=7 correct, >=4 partial, else
 *  incorrect) since `overallAssessment` is no longer carried over the wire. */
function AnalysisHeader({ assessment }: { assessment: Feedback }) {
  const score = assessment.score;
  const scoreCategory = score === undefined ? undefined : correctnessFromScore(score);  // canonical >=7 / >=4
  return (
    <div className="assessment-section">
      <div className="assessment-header">
        {score !== undefined && (
          <span className={`score ${badgeClass(scoreCategory, ASSESSMENT_CLASSES)}`}>
            {score}/10
          </span>
        )}
      </div>
      {assessment.computedScores && (
        <div className="scores-row">
          {assessment.computedScores.correctnessScore !== undefined && <span>{SCORING_LABELS.correctness}: {assessment.computedScores.correctnessScore}</span>}
          {assessment.computedScores.speedScore !== undefined && <span>{SCORING_LABELS.speed}: {assessment.computedScores.speedScore}</span>}
          {assessment.computedScores.finalScore !== undefined && <span>Final: {assessment.computedScores.finalScore}</span>}
        </div>
      )}
    </div>
  );
}

interface ParsedBodyProps {
  output: ModelOutput;
  promptType?: PromptType;
  /** The student images this run sent, in order — the only images its
   *  boxes may be drawn on. */
  sentStudentImages?: { url: string; label?: string }[];
  /** Page images an extraction sent to the model — the overlay target for its
   *  per-page diagram boxes. */
  pageImages?: { url: string; label: string }[];
}

/** Page-specific wrapper around the shared `ParsedFeedbackBody`. Adds an
 *  AnalysisHeader (assessment badge + score chips) when the schema is
 *  score-bearing, and preserves the legacy "show only the score header
 *  when feedback exists but no schema is registered" behaviour. */
function ParsedOutputBody({ output, promptType, sentStudentImages, pageImages }: ParsedBodyProps) {
  const schema = resolveOutputSchemaType(promptType);
  const isScoreBearing = schema === 'ObjectiveCorrectnessV1' || schema === 'FreeformRubricV1'
    || schema === 'StructuredAssessmentV1';

  // Legacy path: no registered schema, but a Feedback was parsed anyway.
  // Surface just the score header — the underlying body's JSON-tree fallback
  // would be redundant here since we already have a structured assessment.
  if (!schema && output.parsedAssessment) {
    return <AnalysisHeader assessment={output.parsedAssessment} />;
  }

  return (
    <>
      {isScoreBearing && output.parsedAssessment && (
        <AnalysisHeader assessment={output.parsedAssessment} />
      )}
      <ParsedFeedbackBody
        promptType={promptType}
        outputSchemaType={schema}
        feedback={output.parsedAssessment}
        extractedData={output.extractedData}
        rawResponse={getRawOutputText(output)}
        sentStudentImages={sentStudentImages}
        pageImages={pageImages}
      />
    </>
  );
}

// ── Output Body (per-type via composition) ───────────────────

interface OutputBodyProps {
  output: ModelOutput;
  collapsedSections: Record<string, boolean>;
  onToggle: (id: string) => void;
  /** The prompt's type — drives schema-aware parsed-output dispatch
   *  (`ObjectiveCorrectnessV1` vs `VisualsV1` etc.). Optional because
   *  a few legacy paths render outputs without a known prompt type. */
  promptType?: PromptType;
  /** Resolved student solution image URL — passed to the visuals parsed view
   *  so `extract_visuals` runs can overlay their bboxes on it. */
  solutionImageUrl?: string;
}

function VoiceOutputBody({ output, collapsedSections, onToggle }: OutputBodyProps) {
  return (
    <>
      <div className="metadata-grid" style={{ padding: '16px 16px 0' }}>
        {output.status && (
          <div className="meta-item">
            <label>Status</label>
            <span className={`assessment-badge ${badgeClass(output.status, STATUS_CLASSES)}`}>{output.status}</span>
          </div>
        )}
        <div className="meta-item"><label>Duration</label><span>{formatDuration(output.latencyMs)}</span></div>
        <div className="meta-item"><label>Estimated Cost</label><span>{formatCost(output.cost)}</span></div>
      </div>
      <CollapsibleSection id="rawOutput" title="Transcript" collapsed={!!collapsedSections.rawOutput} onToggle={onToggle}>
        <div style={{ padding: 16, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {output.rawOutput || 'No transcript recorded'}
        </div>
      </CollapsibleSection>
    </>
  );
}

// ── Strategy: RunTypeConfig ──────────────────────────────────
//
// Voice runs render fundamentally differently — duration instead of token
// usage, a transcript section instead of Raw Response + Parsed Output, and
// a Session Status row in metadata. Every other prompt family (analyze /
// hint / extract_steps / extract_visuals / unknown) shares one default
// rendering (the Parsed Output section is itself schema-dispatched via
// `ParsedFeedbackBody`, so per-family configs aren't necessary).

interface RunTypeConfig {
  Performance: ComponentType<{ output: ModelOutput }>;
  metadataExtra?: (output: ModelOutput) => ReactNode;
}

const voiceConfig: RunTypeConfig = {
  Performance: DurationPerformance,
  metadataExtra: (output) => output.status ? (
    <div className="meta-item">
      <label>Session Status</label>
      <span className={`assessment-badge ${badgeClass(output.status, STATUS_CLASSES)}`}>{output.status}</span>
    </div>
  ) : null,
};

const defaultConfig: RunTypeConfig = {
  Performance: TokenPerformance,
};

function getRunTypeConfig(promptType?: PromptType): RunTypeConfig {
  if (promptType && stepTypeFamily(promptType) === 'voice') return voiceConfig;
  return defaultConfig;
}

// ── Main Component ───────────────────────────────────────────

export function ModelRunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Prev/next within the original list view. The list page stashes the
  // filtered id list in router state when a row is clicked.
  // The accompanying `returnTo` (shared-ui/navigation) is consumed by the
  // BackButton below — no need to read it here.
  const siblingIds = (location.state as { ids?: string[] } | null)?.ids;
  const currentIndex = siblingIds && id ? siblingIds.indexOf(id) : -1;
  const prevId = currentIndex > 0 ? siblingIds![currentIndex - 1] : undefined;
  const nextId = currentIndex >= 0 && currentIndex < (siblingIds?.length ?? 0) - 1
    ? siblingIds![currentIndex + 1]
    : undefined;
  const goSibling = (siblingId: string) => navigate(
    `/model-data/${siblingId}`,
    { state: { ids: siblingIds }, replace: true },
  );
  const [showRelated, setShowRelated] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    parameters: false, promptTemplate: false,
    rawOutput: true, inputDetails: false, parsedOutput: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['modelOutput', id],
    queryFn: () => adminApi.getModelOutputById(id!),
    enabled: !!id,
    // A call still in flight: refresh until it is finished.
    refetchInterval: q => (q.state.data?.output.status === 'running' ? 5000 : false),
  });
  const { data: promptsData } = useQuery({ queryKey: ['prompts'], queryFn: () => adminApi.getPrompts() });
  const { data: usersData } = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: () => adminApi.getUsers({ pageSize: 100 }) });

  // Resolve the run's owner by id directly — the 100-user page above misses
  // anyone outside it, which is why the User field fell back to a raw id.
  const runUserId = data ? (data.output.userId || data.input.userId) : undefined;
  // `retry: false` — a run's owner can legitimately have no user doc: eval
  // runs are recorded as `eval-system-user`, which is seeded in dev and not in
  // prod. The name falls back to the raw id below, so retrying a 404 buys
  // nothing and just bills it to the error log three times over.
  const { data: runUser } = useQuery({
    queryKey: ['user', runUserId],
    queryFn: () => userApi.getUserById(runUserId!),
    enabled: !!runUserId,
    retry: false,
  });

  const promptMap = useMemo(() => {
    const map = new Map<string, PromptInfo>();
    promptsData?.forEach((p: PromptInfo) => map.set(p.id, p));
    return map;
  }, [promptsData]);

  const getUserName = (userId?: string): string => {
    if (!userId) return '-';
    const user = usersData?.items.find(u => u.id === userId);
    return user?.name || userId.substring(0, 8) + '...';
  };

  const selectedPromptId = data?.output.parameters.promptId;
  const { data: promptHistory } = useQuery({
    queryKey: ['promptHistory', selectedPromptId],
    queryFn: () => adminApi.getPromptHistory(selectedPromptId!),
    enabled: !!selectedPromptId,
  });

  const toggleSection = (id: string) => setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const promptVersionTemplate = useMemo(() => {
    if (!data?.output || !promptHistory) return undefined;
    const match = promptHistory.find((v: PromptVersionInfo) => v.version === data.output.parameters.promptVersion);
    return match?.userPrompt;
  }, [data?.output, promptHistory]);

  if (isLoading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {(error as Error).message}</div>;
  if (!data) return <div className="error">Model run not found</div>;

  const { output, input, chatMessages } = data;
  const promptInfo = promptMap.get(output.parameters.promptId);
  const promptType: PromptType | undefined = promptInfo?.type;

  // Single dispatch — all type-specific rendering flows from this config
  const config = getRunTypeConfig(promptType);
  const { Performance } = config;
  const isVoice = !!(promptType && stepTypeFamily(promptType) === 'voice');
  // Question-generation runs author from a PDF, not a student attempt — the
  // Question/Student/Assignment input context is all empty/runtime placeholders,
  // so drop the whole Model Input card (mirrors GenerationDetail).
  const isGeneration = promptType === 'generate_variations' || promptType === 'generate_variations_from_questions';
  // Session-report runs synthesise from a digested JSON blob (qsr.input), not a
  // student's answer image or a single question. The submission-image block and
  // the (global-wildcard) Question group are always empty here, so drop them.
  const isSessionReport = promptType === 'generate_question_session_report';
  // Chemistry-lab runs take a mix, not an attempt: the question/student/image
  // panels would be all placeholders, so they get a view of their own.
  const isChemistryLab = (promptType != null && CHEMISTRY_LAB_PROMPT_TYPES.has(promptType))
    || (input?.operation != null && CHEMISTRY_LAB_OPERATIONS.has(input.operation));
  const isExtraction = (promptType != null && EXTRACTION_PROMPT_TYPES.has(promptType))
    || input?.operation === 'extract_answer_key';

  // Images an operation attached that are NOT a student's answer canvas — the
  // source-document pages an extraction read. The Submission Image panel keys
  // off `studentSolution.imageUrl`, which these never populate, so without this
  // the panel showed "✓ sent to model" next to "No image submitted".
  const attachedOperationImages = input?.operationContext?.attachedImages ?? [];

  // What the call carried, per the run's own `attachedImages`. The ModelInput
  // says what was RESOLVED; only this says what was sent.
  const studentImagesSent = (output.attachedImages ?? [])
    .filter(img => img.role === 'student').length;
  // The student images exactly as sent, when the run recorded where each came
  // from: these, in order, are what the model read.
  // The student images exactly as this run sent them — the one rule every view
  // of the run uses (utils/attachedImages.ts).
  const sentStudentImages = sentStudentImagesOf(
    output.attachedImages, input?.studentSolution?.imageUrl, input?.studentSolution?.pageImageUrls);

  // Error banner text — new rows persist only structured errorRecords; the
  // legacy `errors[]` string array was a duplicate. Fall back to it for
  // pre-taxonomy rows.
  const errorMessages = output.errors?.length
    ? output.errors
    : (output.errorRecords?.map(r => r.message).filter(Boolean) ?? []);
  const errorBannerText = errorMessages.length ? errorMessages.join(' | ') : undefined;

  return (
    <div className="model-run-detail operation-detail-page">
      {/* Header */}
      <div className="page-header">
        <BackButton label="Model Runs" fallback="/model-data" />
        <div className="header-content">
          <div className="header-title">
            <h1>Model Run</h1>
            {/* Prompt executed — the run's identity at a glance. Date lives in
                Run Metadata below, so it is not repeated here. */}
            <span className="submitted-at">{promptInfo?.name ?? output.parameters.promptId}</span>
          </div>
          <div className="header-actions">
            {siblingIds && currentIndex >= 0 && (
              <>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => prevId && goSibling(prevId)}
                  disabled={!prevId}
                  title="Previous run in the current list"
                >
                  &larr; Prev
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', alignSelf: 'center' }}>
                  {currentIndex + 1} / {siblingIds.length}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => nextId && goSibling(nextId)}
                  disabled={!nextId}
                  title="Next run in the current list"
                >
                  Next &rarr;
                </button>
              </>
            )}
            <button className="refresh-btn" onClick={() => queryClient.invalidateQueries({ queryKey: ['modelOutput', id] })} disabled={isLoading} title="Refresh data">
              &#x21bb; Refresh
            </button>
          </div>
        </div>
        {/* Actions on this run, on their own line — apart from moving between runs. */}
        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <ReplayButton output={output} />
          <span className="header-divider" style={{ color: 'var(--text-tertiary)' }}>|</span>
          <button
            className="reeval-button"
            onClick={() => setShowRelated(true)}
            title="The other steps of this pipeline run, other runs on the same submission or job, and admin re-runs."
          >
            Related
          </button>
        </div>
      </div>

      <ReplayBanner output={output} getUserName={getUserName} />

      {(() => {
        // The row exists from the call's start (model-output-lifecycle-target.md):
        // say so, rather than leave the empty response to look like a failure.
        const live = runLiveState(output);
        if (!live) return null;
        return (
          <div className="card" style={{ borderLeft: `4px solid ${live.state === 'orphaned' ? '#9ca3af' : '#3b82f6'}` }}>
            {live.state === 'running'
              ? <>This call is still running on <strong>{live.provider}</strong>
                  {output.hop ? <> (failover hop {output.hop})</> : null} — {live.elapsed} so far. The prompt below is what was
                  sent; the response, tokens and cost appear when it returns. This page refreshes itself.</>
              : <>This call was still running {live.elapsed} after it started on <strong>{live.provider}</strong>, past its
                  timeout — the process running it is gone. The prompt below is what was sent; nothing came back.</>}
          </div>
        );
      })()}

      {input.pending && (
        // The runner's placeholder: the call is recorded, its operation has not
        // saved what it sent yet (model-output-lifecycle-target.md, input side).
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          This run's inputs — the question and its context — are saved when its operation
          finishes, and it has not yet: it is still running, or it stopped before saving them.
          The prompt and response below are complete.
        </div>
      )}

      {/* Run Metadata */}
      <div className="card">
        <h3>Run Metadata</h3>
        <div className="metadata-grid">
          {/* The id of THIS row. One import fans out to a dozen model runs whose
              metadata is otherwise identical — same date, pipeline, step, prompt —
              so without it a run can only be named by the URL you arrived from. */}
          <div className="meta-item">
            <label>Run ID</label>
            <IdControl value={output.id} container="modelOutputs" />
          </div>
          {/* What this particular call covered — 'pages 1–4', 'Q17–Q28'. The one
              field that tells two sibling runs apart. */}
          {input.operationContext?.runInfo && (
            <div className="meta-item">
              <label>Covers</label>
              <span>{input.operationContext.runInfo}</span>
            </div>
          )}
          <div className="meta-item">
            <label>User</label>
            {(() => {
              const uid = output.userId || input.userId;
              return uid
                ? <IdControl value={uid} container="users" display={runUser?.name ?? getUserName(uid)} />
                : <span>-</span>;
            })()}
          </div>
          <div className="meta-item"><label>Date</label><span>{formatDateTimePrecise(output.generatedAt)}</span></div>
          <div className="meta-item"><label>Source</label><span>{output.source}</span></div>
          {output.submissionId && (
            <div className="meta-item">
              <label>Submission ID</label>
              <IdControl value={output.submissionId} container="submissions" />
            </div>
          )}
          {output.pipelineName && <div className="meta-item"><label>Pipeline</label><span>{output.pipelineName}</span></div>}
          {output.stepIndex != null && <div className="meta-item"><label>Step</label><span>{output.stepIndex}</span></div>}
          {/* Prompt name + type in one field (name links out to the prompt). */}
          <div className="meta-item">
            <label>Prompt</label>
            <span>
              <IdControl
                value={output.parameters.promptId}
                container="prompts"
                display={`${promptInfo?.name || output.parameters.promptId} (v${output.parameters.promptVersion})`}
              />
              {promptType && <span style={{ color: 'var(--text-tertiary)', marginLeft: 6 }}>· {promptType}</span>}
            </span>
          </div>
          <div className="meta-item"><label>Status</label><RunStatusCell run={output} /></div>
          {errorBannerText && (
            <div className="meta-item" style={{ gridColumn: '1 / -1' }}>
              <label>Error</label>
              <span style={{ color: 'var(--color-error, #ef4444)', whiteSpace: 'pre-wrap' }}>{errorBannerText}</span>
            </div>
          )}
          <div className="meta-item">
            <label>LLM Provider</label>
            <span>
              {output.parameters.provider}
              {output.parameters.model ? ` (${output.parameters.model})` : ''}
            </span>
          </div>
          {config.metadataExtra?.(output)}
          <div className="meta-item">
            <label>Image</label>
            <span
              title={output.attachedImages
                ? (output.attachedImages.length > 0
                  ? `Images attached to this LLM call, in order: ${output.attachedImages.map(i => i.label ? `${i.role} "${i.label}"` : i.role).join(', ')}.`
                  : 'This LLM call was text-only. For pipeline steps this is expected when the step consumes extracted text from an earlier step instead of the raw image.')
                : undefined}
              style={output.attachedImages?.length === 0
                ? { color: 'var(--color-warning)' } : undefined}
            >
              {output.attachedImages
                ? (output.attachedImages.length > 0
                  ? `✓ ${output.attachedImages.length} sent to model`
                  : 'not sent to model (text-only call)')
                : <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>not available</span>}
            </span>
          </div>
          {/* Labels ride next to their images as separate text parts, so they
              never show up in the prompt body below — this row is the only
              place they are visible. */}
          {output.attachedImages?.some(i => i.label) && (
            <div className="meta-item">
              <label>Image labels</label>
              <span
                style={{ fontStyle: 'italic' }}
                title="Each was sent as a text part immediately before its image. Not part of the prompt text."
              >
                {output.attachedImages.filter(i => i.label).map(i => `[${i.label}]`).join(' ')}
              </span>
            </div>
          )}
          {(() => {
            // Question/part diagram attachment — only rendered when the
            // question actually has diagrams (or the run recorded some).
            const partDiagrams = input.question?.parts?.filter(p => p.diagramUrl).length ?? 0;
            const diagramCount = (input.question?.imageUrl ? 1 : 0) + partDiagrams;
            const sent = (output.attachedImages ?? []).filter(i => i.role === 'question');
            if (diagramCount === 0 && sent.length === 0) return null;
            return (
              <div className="meta-item">
                <label>Question Images</label>
                <span
                  title={sent.length > 0
                    ? 'These question/part diagram images were attached to this LLM call (each labeled inline before its image).'
                    : 'The question has diagram(s) but none were attached to this call — the prompt does not reference {{q.text}}, or this run predates question-image attachment.'}
                  style={sent.length === 0 ? { color: 'var(--color-warning)' } : undefined}
                >
                  {sent.length > 0 ? `✓ sent: ${sent.join(', ')}` : 'not sent to model'}
                </span>
              </div>
            );
          })()}
        </div>
      </div>

      <ModelRunReplays output={output} getUserName={getUserName} />

      {/* Run Performance */}
      <div className="card">
        <h3>Run Performance</h3>
        <div className="perf-grid">
          <Performance output={output} />
          {output.matchScore !== undefined && (
            <div className="perf-item"><label>Match Score</label><span className="perf-value">{output.matchScore < 0 ? 'N/A' : output.matchScore}</span></div>
          )}
        </div>
      </div>

      {/* Model Input */}
      {!isGeneration && (
      <div className="card">
        <h3>Model Input</h3>
        <CollapsibleSection id="inputDetails" title="Input Details" collapsed={!!collapsedSections.inputDetails} onToggle={toggleSection}>
          <div style={{ padding: 16 }}>
            {isChemistryLab ? (
              <div className="input-context">
                <ChemistryLabInputView input={input} />
              </div>
            ) : isExtraction ? (
              <div className="input-context">
                <ExtractionInputView input={input} promptType={promptType} sentCount={output.attachedImages?.length} />
              </div>
            ) : (
            <div className="input-context">
              {!isSessionReport && (
              <div className="input-images">
                  <fieldset className="image-block">
                    <legend>
                      {/* Extraction reads a worksheet, not a student's answer —
                          calling that a "submission" misreads the run. */}
                      <span>{attachedOperationImages.length > 0 ? 'Source Pages Sent To Model' : 'Submission Image'}</span>
                      {/* Source pages are recorded under their own role (`context`),
                          not `student` — asking whether a STUDENT image went
                          labelled every extraction call "not sent to model" above
                          the very pages it sent. */}
                      {output.attachedImages && attachedOperationImages.length > 0 && (
                        <span
                          className="tag"
                          style={output.attachedImages.length > 0
                            ? { backgroundColor: 'var(--status-success-bg)', color: 'var(--status-success-text)' }
                            : { backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}
                          title="How many images this LLM call carried, as recorded on the run."
                        >
                          {output.attachedImages.length > 0 ? `✓ ${output.attachedImages.length} sent to model` : 'not sent to model'}
                        </span>
                      )}
                      {output.attachedImages && attachedOperationImages.length === 0 && (
                        <span
                          className="tag"
                          style={sentStudentImage(output.attachedImages)
                            ? { backgroundColor: 'var(--status-success-bg)', color: 'var(--status-success-text)' }
                            : { backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}
                          title={sentStudentImage(output.attachedImages)
                            ? 'The image was attached to this LLM call.'
                            : 'This LLM call did not carry the student\'s image. It is shown below for reference only.'}
                        >
                          {sentStudentImage(output.attachedImages) ? '✓ sent to model' : 'not sent to model'}
                        </span>
                      )}
                      {/* Count what the CALL carried, from the run's own record
                          of it — not from the input we resolved. Multi-page work
                          is sent as several images, and "sent to model" alone
                          cannot say whether that was one or three. On prod
                          submission 85720a18 the difference between a joined
                          image and its pages was the whole bug. */}
                      {studentImagesSent > 0 && (
                        <span
                          className="tag"
                          title="How many images this LLM call actually carried, as recorded on the run."
                        >
                          {studentImagesSent} image{studentImagesSent === 1 ? '' : 's'} sent
                        </span>
                      )}
                    </legend>
                    {/* Multi-page work reaches the readers as PAGES; the
                        composite exists only so something can show one picture.
                        Displaying the join here would show a seam the model was
                        never given and invite a diagnosis of the wrong thing —
                        which is exactly the mistake the joined image caused on
                        prod submission 85720a18. */}
                    {sentStudentImages.length > 0
                      ? (
                        <div className="operation-images">
                          {sentStudentImages.map((img, i) => (
                            <figure key={`${img.url}-${i}`} className="operation-image">
                              <ImageViewer
                                src={img.url}
                                alt={img.label ?? `Student image ${i + 1}`}
                                className="model-run-detail-thumb"
                              />
                              <figcaption>{img.label ?? `Image ${i + 1} of ${sentStudentImages.length}`}</figcaption>
                            </figure>
                          ))}
                        </div>
                      )
                      : studentImagesSent > 1 && (input.studentSolution?.pageImageUrls?.length ?? 0) > 1
                      ? (
                        <div className="operation-images">
                          {input.studentSolution!.pageImageUrls!.map((url, i) => (
                            <figure key={url} className="operation-image">
                              <ImageViewer
                                src={url}
                                alt={`Student solution page ${i + 1}`}
                                className="model-run-detail-thumb"
                              />
                              <figcaption>
                                Page {i + 1} of {input.studentSolution!.pageImageUrls!.length}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      )
                      : input.studentSolution?.imageUrl
                      ? (
                        <>
                          <ImageViewer src={input.studentSolution?.imageUrl} alt="Student solution" className="model-run-detail-thumb" />
                          {output.attachedImages && !sentStudentImage(output.attachedImages) && (
                            <div className="no-image-compact">
                              Shown for reference only — this step's LLM call was text-only; the prompt
                              for this step does not request the image.
                            </div>
                          )}
                        </>
                      )
                      : attachedOperationImages.length > 0
                        ? (
                          // Operations like extraction attach source-document pages
                          // rather than a student's answer canvas. Without this the
                          // panel claimed "✓ sent to model" and "No image submitted"
                          // at the same time.
                          <div className="operation-images">
                            {attachedOperationImages.map((img) => (
                              <figure key={img.label} className="operation-image">
                                <ImageViewer src={img.url} alt={img.label} className="model-run-detail-thumb" />
                                <figcaption>{img.label}</figcaption>
                              </figure>
                            ))}
                          </div>
                        )
                        : <div className="no-image-compact">No image submitted</div>}
                  </fieldset>
                  {input.question?.imageUrl && (
                    <fieldset className="image-block">
                      <legend>
                        <span>Question Image</span>
                        <QuestionImageSentTag
                          attachedImages={output.attachedImages}
                          label="Question diagram"
                        />
                      </legend>
                      <ImageViewer src={input.question!.imageUrl} alt="Question" className="model-run-detail-thumb" />
                    </fieldset>
                  )}
                  {input.question?.parts?.filter(p => p.diagramUrl).map(p => (
                    <fieldset key={p.partId} className="image-block">
                      <legend>
                        <span>Part ({p.partId}) Diagram</span>
                        <QuestionImageSentTag
                          attachedImages={output.attachedImages}
                          label={`Diagram for part (${p.partId})`}
                        />
                      </legend>
                      <ImageViewer src={p.diagramUrl!} alt={`Part (${p.partId}) diagram`} className="model-run-detail-thumb" />
                    </fieldset>
                  ))}
                </div>
              )}
              <PromptInputView
                input={input}
                promptType={promptType}
                hideGroups={isSessionReport ? new Set(['Question']) : undefined}
              />
            </div>
            )}
          </div>
        </CollapsibleSection>
      </div>
      )}

      {/* Model Parameters */}
      <div className="card">
        <h3>Model Parameters</h3>
        <CollapsibleSection id="parameters" title="Parameters" collapsed={!!collapsedSections.parameters} onToggle={toggleSection}>
          <div className="metadata-grid" style={{ padding: 16 }}>
            <div className="meta-item"><label>Prompt Name</label><span>{promptInfo?.name || output.parameters.promptId}</span></div>
            <div className="meta-item"><label>Version</label><span>v{output.parameters.promptVersion}</span></div>
            <div className="meta-item"><label>LLM Model</label><span className="tag">{output.parameters.model || output.parameters.provider || '-'}</span></div>
            <ActualParametersFields parameters={output.parameters} />
          </div>
        </CollapsibleSection>
        {output.systemPromptText && (
          <CollapsibleSection
            id="systemPromptTemplate"
            title="System Prompt Sent to LLM"
            collapsed={!!collapsedSections.systemPromptTemplate} onToggle={toggleSection}
            titleAccessory={<>
              <TextViewerButton text={output.systemPromptText} title="System Prompt Sent to LLM" />
              <JsonViewerButton text={output.systemPromptText} title="System Prompt Sent to LLM" />
            </>}
          >
            <pre className="code-block">{output.systemPromptText}</pre>
          </CollapsibleSection>
        )}
        {chatMessages && chatMessages.length > 0 && (
          <CollapsibleSection
            id="chatTranscript"
            title={`Conversation (${chatMessages.length} message${chatMessages.length === 1 ? '' : 's'})`}
            collapsed={!!collapsedSections.chatTranscript} onToggle={toggleSection}
            titleAccessory={<JsonViewerButton text={JSON.stringify(chatMessages, null, 2)} title="Chat Messages (reconstructed)" />}
          >
            <div className="chat-transcript">
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`chat-message chat-message--${msg.role}${msg.isCurrentTurn ? ' chat-message--current' : ''}`}
                >
                  <div className="chat-message-header">
                    <span className={`role-chip role-chip--${msg.role}`}>{msg.role}</span>
                    {msg.isCurrentTurn && <span className="current-turn-marker">this turn</span>}
                    {msg.imageAction && <span className="image-action-tag">canvas {msg.imageAction}</span>}
                  </div>
                  {msg.content && <div className="chat-message-content">{msg.content}</div>}
                  {msg.imageUrl && (
                    <a href={msg.imageUrl} target="_blank" rel="noopener noreferrer" className="chat-message-image">
                      <img src={msg.imageUrl} alt={`Canvas at ${msg.role} turn`} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}
        {(() => {
          const promptText = output.promptText || promptVersionTemplate || '';
          const promptTitle = output.promptText
            ? (output.systemPromptText ? 'User Prompt Sent to LLM' : 'Prompt Sent to LLM')
            : `Prompt Template (v${output.parameters.promptVersion})`;
          return (
            <CollapsibleSection
              id="promptTemplate"
              title={promptTitle}
              collapsed={!!collapsedSections.promptTemplate} onToggle={toggleSection}
              titleAccessory={<>
                <TextViewerButton text={promptText} title={promptTitle} />
                <JsonViewerButton text={promptText} title={promptTitle} />
              </>}
            >
              <pre className="code-block">{output.promptText || promptVersionTemplate || 'Loading...'}</pre>
            </CollapsibleSection>
          );
        })()}
      </div>

      {/* View for user — the run's output through the student's own component,
          for prompt types that have one. Renders nothing otherwise. */}
      <StudentViewSection promptType={promptType} rawResponse={getRawOutputText(output)} />

      {/* Model Output — voice runs render a transcript; every other run type
          uses the shared ModelOutputView (identical to the operation
          Submission/Hint run-detail card). */}
      <div className="card">
        <h3>Model Output</h3>
        {isVoice ? (
          <>
            {errorBannerText && (
              <div className="parse-error-banner">
                <strong>{errorMessages.length === 1 ? 'Error:' : 'Errors:'}</strong> {errorBannerText}
              </div>
            )}
            <QualityCheckBadges qualityChecks={output.qualityChecks} />
            <VoiceOutputBody output={output} collapsedSections={collapsedSections} onToggle={toggleSection} />
          </>
        ) : (
          <ModelOutputView
            error={errorBannerText}
            errorRecords={output.errorRecords}
            qualityBadges={<QualityCheckBadges qualityChecks={output.qualityChecks} />}
            reasoningSummary={output.reasoningSummaryText}
            rawOutputFull={output.rawOutputFull}
            rawResponse={getRawOutputText(output)}
            parsed={<ParsedOutputBody output={output} promptType={promptType} sentStudentImages={sentStudentImages} pageImages={attachedOperationImages} />}
            parsedTitle="Parsed Output"
          />
        )}
      </div>
      {showRelated && <RelatedModelRunsDialog outputId={output.id} onClose={() => setShowRelated(false)} />}
    </div>
  );
}
